import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import type { ReactNode } from "react";
import { useCallback, useId } from "react";

import { cn } from "../lib/cn";
import { FOCUS_RING, REDUCED_MOTION_SAFE } from "../lib/focus";
import { OVERLAY_FOOTER_LAYOUT } from "../lib/overlayFooter";

/**
 * A drawer is an edge-anchored PANEL — navigation, filters, an admin inspector
 * — that stays a panel at every viewport.
 *
 * It is not the platform's transient overlay. A create/edit form, a picker, or
 * a confirm uses `Dialog`, which is a bottom sheet on a phone and a centered
 * dialog on a desktop from one component. Reaching for `Drawer` because a
 * screen wants a form on the side is the mistake this component's design
 * exists to prevent.
 */
export const drawerVariants = cva(
  cn(
    "fixed z-[var(--sm-z-drawer)] flex flex-col bg-surface-elevated shadow-lg",
    REDUCED_MOTION_SAFE,
  ),
  {
    variants: {
      side: {
        left: cn(
          "inset-y-0 left-0 h-full w-[min(20rem,85vw)] rounded-r-lg",
          "data-[state=open]:animate-drawer-left-in data-[state=closed]:animate-drawer-left-out",
        ),
        right: cn(
          "inset-y-0 right-0 h-full w-[min(20rem,85vw)] rounded-l-lg",
          "data-[state=open]:animate-drawer-right-in data-[state=closed]:animate-drawer-right-out",
        ),
      },
    },
    defaultVariants: { side: "right" },
  },
);

export type typeDrawerProps = VariantProps<typeof drawerVariants> & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Required. Every overlay is announced by name. */
  title: string;
  description?: string | undefined;
  children: ReactNode;
  /** Pinned below the scrolling body, same rule as `Dialog`. */
  footer?: ReactNode | undefined;
  /** See `Dialog` — the guard behaves identically here. */
  isDirty?: boolean | undefined;
  onRequestClose?: (() => void) | undefined;
  className?: string | undefined;
};

const CloseIcon = () => (
  <svg
    className="h-icon-md w-icon-md"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="m5 5 10 10M15 5 5 15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * An edge-anchored panel on Radix's Dialog primitive, so it gets the same focus
 * trap, focus restore, scroll lock, and Escape handling as every other overlay.
 */
export const Drawer = ({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  side,
  isDirty = false,
  onRequestClose,
  className,
}: typeDrawerProps): ReactNode => {
  const generatedId = useId();
  const titleId = `${generatedId}-title`;

  const requestClose = useCallback(() => {
    if (isDirty) {
      onRequestClose?.();
      return;
    }
    onOpenChange(false);
  }, [isDirty, onOpenChange, onRequestClose]);

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (next) {
          onOpenChange(true);
          return;
        }
        requestClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-[var(--sm-z-scrim)] bg-scrim",
            "data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out",
            REDUCED_MOTION_SAFE,
          )}
        />
        <DialogPrimitive.Content
          aria-labelledby={titleId}
          // No aria-describedby here: Radix ties the content to the
          // Description below by itself once one renders, and appends its own
          // id to anything passed in. An id of ours would be listed next to
          // an id no element carries.
          data-dirty={isDirty ? "true" : undefined}
          data-side={side ?? "right"}
          className={cn(drawerVariants({ side }), className)}
        >
          <div className="flex items-start justify-between gap-3 p-4">
            <div className="flex flex-col gap-1">
              <DialogPrimitive.Title id={titleId} className="text-h2 text-ink">
                {title}
              </DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="text-body text-ink-secondary">
                  {description}
                </DialogPrimitive.Description>
              ) : null}
            </div>
            <button
              type="button"
              onClick={requestClose}
              aria-label="Dismiss"
              className={cn(
                "-m-2 flex h-touch-target w-touch-target shrink-0 items-center justify-center",
                "rounded-sm text-ink-secondary hover:bg-surface-sunken hover:text-ink",
                FOCUS_RING,
              )}
            >
              <CloseIcon />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4">{children}</div>

          {footer ? (
            <div
              data-testid="drawer-footer"
              className={cn(OVERLAY_FOOTER_LAYOUT, "p-4")}
            >
              {footer}
            </div>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
